import React from 'react';
import { Box, Menu, MenuItem, Tab, Tabs, type TabProps, type TabsProps } from '@mui/material';
import { useOverflowTabs } from '../../../../hooks/useOverflowTabs';

type StyledTabsProps = Omit<TabsProps, 'variant'> & {
  children: React.ReactNode;
};

export const StyledTabs = ({ children, sx, ...tabsProps }: StyledTabsProps) => {
  const items = React.Children.toArray(children);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const measurementRef = React.useRef<HTMLDivElement>(null);
  const [moreAnchor, setMoreAnchor] = React.useState<HTMLElement | null>(null);
  const activeIndex = typeof tabsProps.value === 'number' ? tabsProps.value : 0;
  const { displayedIndices, hiddenIndices, hasOverflow } = useOverflowTabs({
    itemCount: items.length,
    activeIndex,
    containerRef,
    measurementRef,
    moreWidth: 112,
    gap: 4,
  });
  const onChange = tabsProps.onChange;

  return (
    <Box
      ref={containerRef}
      sx={{
        mb: 1.5,
        backgroundColor: 'background.default',
        borderBottom: 1,
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      <Tabs
        {...tabsProps}
        onChange={(event, value) => {
          if (value === 'more') {
            if (event.currentTarget instanceof HTMLElement) {
              setMoreAnchor(event.currentTarget);
            }
            return;
          }
          onChange?.(event, value);
        }}
        variant="standard"
        sx={{
          minHeight: 34,
          minWidth: 0,
          width: '100%',
          '& .MuiTabs-flexContainer': {
            gap: 0.5,
            minWidth: 0,
            overflow: 'hidden',
          },
          '& .MuiTabs-scroller': {
            overflow: 'hidden !important',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            minHeight: 34,
            minWidth: 0,
            flexShrink: 1,
            py: 0.5,
            px: 1.5,
            fontSize: '0.8125rem',
          },
          ...sx,
        }}
      >
        {displayedIndices.map((index) => {
          const item = items[index];
          return React.isValidElement<TabProps>(item)
            ? React.cloneElement(item, { value: index })
            : item;
        })}
        {hasOverflow && <Tab value="more" label={`More (${hiddenIndices.length})`} />}
      </Tabs>
      <Box
        ref={measurementRef}
        aria-hidden
        sx={{ position: 'absolute', visibility: 'hidden', top: -10000, whiteSpace: 'nowrap' }}
      >
        {items.map((item, index) => (
          <Box
            key={index}
            data-overflow-tab-index={index}
            sx={{ display: 'inline-flex', px: 1.5, fontSize: '0.8125rem' }}
          >
            {React.isValidElement<{ label?: React.ReactNode }>(item) ? item.props.label : null}
          </Box>
        ))}
      </Box>
      <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}>
        {hiddenIndices.map((index) => {
          const item = items[index];
          const label = React.isValidElement<{ label?: React.ReactNode }>(item)
            ? item.props.label
            : '';
          return (
            <MenuItem
              key={index}
              selected={index === activeIndex}
              onClick={(event) => {
                setMoreAnchor(null);
                onChange?.(event, index);
              }}
            >
              {label}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

export { Tab };
